import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { planNotices, reminderIdsOf } from '../../lib/agendaNotices.js';
import { armReminders, getAppointments, getReminders } from '../../lib/api/personal.js';
import { characterImage, DEFAULT_CHARACTER } from '../../lib/characters.js';
import {
  clearMessageNotices,
  notifyPermission,
  onNoticeTap,
  requestNotifyPermission,
  setNotifyPrefs,
  syncScheduledNotices,
} from '../../lib/notify.js';
import { firstDeliveryDone, NOTIFY_ASK_EVENT } from '../../lib/firstRun.js';
import { isNativeShell } from '../../lib/platform.js';
import { useProfile } from '../../lib/useProfile.js';
import '../messaging/recovery-sheet.css';

const ASKED_KEY = 'secretario.avisos.perguntado';
const APPOINTMENTS_KEY = ['agenda-appointments', 'avisos'] as const;
const HORIZON_MS = 14 * 24 * 60 * 60 * 1000;
// Telas em que lembretes e compromissos nascem ou mudam.
const AGENDA_SOURCES = ['/agenda', '/conversas/secretario'];

function alreadyAsked(): boolean {
  try {
    return window.localStorage.getItem(ASKED_KEY) === '1';
  } catch {
    return true;
  }
}

function rememberAsked(): void {
  try {
    window.localStorage.setItem(ASKED_KEY, '1');
  } catch {
    /* sem armazenamento: pergunta de novo na próxima abertura */
  }
}

/**
 * Avisos do aparelho: guarda as escolhas de Ajustes, agenda lembretes e
 * compromissos no app instalado e pergunta, uma vez, se pode avisar.
 */
export function Notifier() {
  const native = isNativeShell();
  const profileQ = useProfile();
  const queryClient = useQueryClient();
  const [location, navigate] = useLocation();
  const [granted, setGranted] = useState(false);
  const [asking, setAsking] = useState(false);
  // Motivo concreto do pedido (ex.: acabou de criar um lembrete); sem motivo, o texto geral.
  const [reason, setReason] = useState<string | null>(null);

  const stored = profileQ.data?.preferences.notifications as Record<string, unknown> | undefined;
  const messages = stored?.messages !== false;
  const reminders = stored?.reminders !== false;
  const secretary = stored?.secretary !== false;
  // "Tocar como despertador": o lembrete sai no volume de alarme (Android).
  const alarmMode = stored?.alarm === true;
  // "Falar o lembrete": o aparelho lê o aviso em voz alta (Android).
  const speakMode = stored?.speak === true;
  // Lembretes que este aparelho já confirmou ao servidor como agendados.
  const armed = useRef(new Set<string>());
  useEffect(() => {
    setNotifyPrefs({ messages, reminders, secretary });
  }, [messages, reminders, secretary]);

  // Toque num aviso abre a tela dele; voltar ao app limpa os avisos de mensagem.
  useEffect(() => {
    if (!native) return;
    let stop: (() => void) | undefined;
    let cancelled = false;
    void onNoticeTap((route) => navigate(route)).then((remove) => {
      if (cancelled) remove();
      else stop = remove;
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') void clearMessageNotices();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      stop?.();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [native, navigate]);

  // Permissão: quem já deu segue direto; quem nunca foi perguntado vê a folha uma vez.
  useEffect(() => {
    if (!native) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    void notifyPermission().then((state) => {
      if (state === 'granted') setGranted(true);
      // O pedido geral só depois da primeira entrega: antes disso, quem pede é o próprio lembrete.
      else if (state === 'prompt' && !alreadyAsked() && firstDeliveryDone()) {
        timer = setTimeout(() => {
          // Outra folha aberta (proteção das conversas) tem prioridade; fica para a próxima.
          if (!document.querySelector('.rk-backdrop, .cv-sheetwrap')) setAsking(true);
        }, 1600);
      }
    });
    return () => clearTimeout(timer);
  }, [native]);

  useEffect(() => {
    if (!native) return;
    const ask = (event: Event) => {
      const detail = (event as CustomEvent<{ reason?: string }>).detail;
      void notifyPermission().then((state) => {
        if (state !== 'prompt') return;
        setReason(detail?.reason ?? null);
        setAsking(true);
      });
    };
    window.addEventListener(NOTIFY_ASK_EVENT, ask);
    return () => window.removeEventListener(NOTIFY_ASK_EVENT, ask);
  }, [native]);

  const remindersQ = useQuery({
    queryKey: ['reminders'],
    queryFn: getReminders,
    staleTime: 15_000,
    refetchInterval: 10 * 60_000,
    enabled: native && granted,
  });
  const appointmentsQ = useQuery({
    queryKey: APPOINTMENTS_KEY,
    queryFn: () => {
      const from = new Date();
      return getAppointments(
        from.toISOString(),
        new Date(from.getTime() + HORIZON_MS).toISOString(),
      );
    },
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
    enabled: native && granted,
  });

  // Saiu da Agenda ou da conversa com o Secretário: a lista pode ter mudado.
  const previous = useRef(location);
  useEffect(() => {
    const from = previous.current;
    previous.current = location;
    if (!native || !granted || from === location) return;
    if (AGENDA_SOURCES.some((path) => from.startsWith(path))) {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      void queryClient.invalidateQueries({ queryKey: APPOINTMENTS_KEY });
    }
  }, [location, native, granted, queryClient]);

  const reminderList = remindersQ.data;
  const appointmentList = appointmentsQ.data;
  useEffect(() => {
    if (!native || !granted || !reminderList || !appointmentList) return;
    const planned = planNotices(reminderList, appointmentList, {
      alarm: alarmMode,
      speak: speakMode,
    });
    void syncScheduledNotices(planned)
      .then((result) => {
        // Só o alarme exato garante a hora: é o que dispensa o aviso de reserva do servidor.
        if (!result.exact) return;
        const fresh = reminderIdsOf(result.keys).filter((id) => !armed.current.has(id));
        if (fresh.length === 0) return;
        return armReminders(fresh).then(() => fresh.forEach((id) => armed.current.add(id)));
      })
      .catch(() => {});
  }, [native, granted, reminders, alarmMode, speakMode, reminderList, appointmentList]);

  if (!asking) return null;
  const characterKey = profileQ.data?.characterKey ?? DEFAULT_CHARACTER;

  function later() {
    rememberAsked();
    setAsking(false);
  }

  // O cartão fecha na hora e o pedido do sistema aparece por cima: esperar a resposta
  // prendia a tela em aparelhos onde ela não volta.
  async function allow() {
    rememberAsked();
    setAsking(false);
    const allowed = await requestNotifyPermission().catch(() => false);
    setGranted(allowed);
    if (allowed) {
      const [{ getMatrixClient }, push] = await Promise.all([
        import('../../lib/matrix/client.js'),
        import('../../lib/push.js'),
      ]);
      const client = getMatrixClient();
      if (client) void push.registerPush(client, navigate).catch(() => {});
    }
  }

  return createPortal(
    <div
      className="rk-backdrop"
      role="dialog"
      aria-label="Notificações"
      onClick={(event) => {
        if (event.target === event.currentTarget) later();
      }}
    >
      <div className="rk-sheet">
        <span className="rk-handle" aria-hidden="true" />
        <div className="rk-stage">
          <img src={characterImage(characterKey, 'simpatico')} alt="" />
          <span className="rk-lock" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path
                d="M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 2h-14l1.5-2Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinejoin="round"
              />
              <path
                d="M10 20.5a2.2 2.2 0 0 0 4 0"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </div>
        <div className="rk-kicker">Notificações</div>
        <h2>{reason ? 'Pra eu te lembrar na hora' : 'Posso te avisar?'}</h2>
        <p>
          {reason ??
            'Notificação quando chegar mensagem e na hora dos seus lembretes. Ela nunca mostra o texto da conversa.'}
        </p>
        <button className="rk-btn" onClick={() => void allow()}>
          Pode avisar
        </button>
        <button className="rk-ghost" type="button" onClick={later}>
          Agora não
        </button>
      </div>
    </div>,
    document.body,
  );
}
